import React, { useState, useContext } from 'react';
import Analytics from '../services/analytics';
import { SimulationInputPanel } from '../components/simulator/SimulationInputPanel';
import { SimulationResultsPanel } from '../components/simulator/SimulationResultsPanel';
import { SimulationParameters, SimulationResult, YearlySimulationResult, HistogramBucket } from '../components/simulator/types';
import { UserContext } from '../context/UserContext';
import { Link } from 'react-router-dom';
import { Save } from 'lucide-react';

interface StockInvestmentSimulatorProps {
  openPopup?: () => void;
}

export function StockInvestmentSimulator({ openPopup }: StockInvestmentSimulatorProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [simulationResult, setSimulationResult] = useState<SimulationResult | null>(null);
  const userContext = useContext(UserContext);

  const runSimulation = async (params: SimulationParameters) => {
    // Track Lead event (High Intent User)
    Analytics.event('Lead', {
      event_category: 'Simulator',
      event_label: 'Run Simulation',
      value: params.initialInvestment,
      currency: 'USD'
    });

    setIsLoading(true);
    // In a real app, we would call an API here
    // For now, we'll simulate a delay and generate mock data
    setTimeout(() => {
      const result = generateSimulationResult(params);
      setSimulationResult(result);
      setIsLoading(false);
    }, 600);
  };

  const handleSave = () => {
    if (userContext?.userData) {
      // User is logged in - Logic to save would go here
      alert('Simulation saved to your profile! (Mock)');
    } else {
      // User is NOT logged in - Trigger capture
      if (openPopup) openPopup();
    }
  };
  return <div className="min-h-screen w-full bg-theme-primary py-8 px-4">
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-theme-primary mb-2">
          Stock Investment Simulator
        </h1>
        <p className="text-theme-secondary">
          Model different investment scenarios and visualize potential
          outcomes
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <SimulationInputPanel onRunSimulation={runSimulation} isLoading={isLoading} />
        <SimulationResultsPanel
          result={simulationResult}
          isLoading={isLoading}
          onSave={handleSave}
          isSaved={false} // Would need real state in future
        />
      </div>
    </div>
  </div>;
}
// Helper function to generate mock simulation results
// Helper function to generate mock simulation results
function generateSimulationResult(params: SimulationParameters): SimulationResult {
  const {
    initialInvestment,
    initialSharePrice,
    numberOfShares,
    timeHorizon,
    yearlyReturns,
    volatilityLevel,
    customVolatility,
    xFactorEvents,
    investmentStrategy
  } = params;

  // Determine volatility based on level
  let volatility = 15; // Default medium
  if (volatilityLevel === 'low') volatility = 10;
  else if (volatilityLevel === 'high') volatility = 25;
  else if (volatilityLevel === 'custom') volatility = customVolatility || 15;

  const numSimulations = 1000;
  const yearlySums = Array(timeHorizon).fill(0).map(() => ({
    portfolioValue: 0,
    sharePrice: 0,
    numberOfShares: 0,
    yearlyReturn: 0,
    totalInvested: 0,
    hysaValue: 0
  }));

  const finalOutcomes: number[] = [];

  for (let i = 0; i < numSimulations; i++) {
    let currentPortfolioValue = initialInvestment;
    let currentSharePrice = initialSharePrice;
    let currentShares = numberOfShares;
    let currentTotalInvested = initialInvestment;
    let currentBenchmarkValue = initialInvestment;

    for (let year = 1; year <= timeHorizon; year++) {
      const yearReturn = yearlyReturns.find(yr => yr.year === year)?.return || 8;
      const volatilityFactor = (Math.random() * 2 - 1) * volatility;
      const actualReturn = yearReturn + volatilityFactor;

      let eventImpact: number | null = null;
      for (const event of xFactorEvents) {
        if (event.enabled && Math.random() * 100 < event.probability) {
          eventImpact = event.impact;
          break;
        }
      }

      const shareGrowth = actualReturn / 100;
      const eventMultiplier = eventImpact ? 1 + eventImpact / 100 : 1;
      currentSharePrice = currentSharePrice * (1 + shareGrowth) * eventMultiplier;

      // Benchmark Update
      const benchmarkRate = 0.05;
      let annualContribution = 0;

      if (investmentStrategy === 'dollar_cost_averaging') {
        annualContribution = (params.weeklyDCAAmount || 0) * 52;
        currentTotalInvested += annualContribution;

        const newShares = annualContribution / currentSharePrice;
        currentShares += newShares;
        currentPortfolioValue = currentShares * currentSharePrice;

        currentBenchmarkValue = (currentBenchmarkValue + annualContribution) * (1 + benchmarkRate);
      } else {
        if (investmentStrategy === 'sell_reinvest') {
          currentShares = currentPortfolioValue / currentSharePrice;
        }
        currentPortfolioValue = currentShares * currentSharePrice;
        currentBenchmarkValue = currentBenchmarkValue * (1 + benchmarkRate);
      }

      // Accumulate sums for averaging later
      yearlySums[year - 1].portfolioValue += currentPortfolioValue;
      yearlySums[year - 1].sharePrice += currentSharePrice;
      yearlySums[year - 1].numberOfShares += currentShares;
      yearlySums[year - 1].yearlyReturn += actualReturn;
      yearlySums[year - 1].totalInvested += currentTotalInvested;
      yearlySums[year - 1].hysaValue += currentBenchmarkValue;
    }
    finalOutcomes.push(currentPortfolioValue);
  }

  // Create Averaged Yearly Results
  const yearlyResults: YearlySimulationResult[] = yearlySums.map((sums, index) => ({
    year: index + 1,
    portfolioValue: sums.portfolioValue / numSimulations,
    sharePrice: sums.sharePrice / numSimulations,
    numberOfShares: sums.numberOfShares / numSimulations,
    yearlyReturn: sums.yearlyReturn / numSimulations,
    totalInvested: sums.totalInvested / numSimulations,
    hysaValue: sums.hysaValue / numSimulations,
    eventImpact: null, // Hard to average events, leave null for summary
    eventDescription: null
  }));

  // Calculate statistics from final outcomes
  finalOutcomes.sort((a, b) => a - b);
  const minValue = finalOutcomes[0];
  const maxValue = finalOutcomes[finalOutcomes.length - 1];
  const finalAverageValue = finalOutcomes.reduce((sum, val) => sum + val, 0) / finalOutcomes.length;

  // Standard Deviation
  const meanDiffSquared = finalOutcomes.map(val => Math.pow(val - finalAverageValue, 2));
  const standardDeviation = Math.sqrt(meanDiffSquared.reduce((sum, val) => sum + val, 0) / finalOutcomes.length);

  // Success Probability
  const totalInvestedFinal = yearlyResults[yearlyResults.length - 1].totalInvested;
  const successCount = finalOutcomes.filter(val => val > totalInvestedFinal).length;
  const successProbability = (successCount / finalOutcomes.length) * 100;

  // Histogram
  const numBuckets = 10;
  const bucketSize = (maxValue - minValue) / numBuckets;
  const histogram: HistogramBucket[] = [];
  for (let i = 0; i < numBuckets; i++) {
    const bucketMin = minValue + i * bucketSize;
    const bucketMax = minValue + (i + 1) * bucketSize;
    const count = finalOutcomes.filter(val => val >= bucketMin && val < bucketMax).length;
    histogram.push({ min: bucketMin, max: bucketMax, count });
  }

  const percentiles: Record<string, number> = {
    '10': finalOutcomes[Math.floor(numSimulations * 0.1)],
    '25': finalOutcomes[Math.floor(numSimulations * 0.25)],
    '50': finalOutcomes[Math.floor(numSimulations * 0.5)],
    '75': finalOutcomes[Math.floor(numSimulations * 0.75)],
    '90': finalOutcomes[Math.floor(numSimulations * 0.9)]
  };

  const totalReturn = ((finalAverageValue - totalInvestedFinal) / totalInvestedFinal) * 100;
  const annualizedReturn = (Math.pow(finalAverageValue / totalInvestedFinal, 1 / timeHorizon) - 1) * 100;

  return {
    parameters: params,
    summary: {
      finalAverageValue,
      minValue,
      maxValue,
      standardDeviation,
      totalReturn,
      annualizedReturn,
      successProbability
    },
    yearlyResults,
    distribution: { histogram, percentiles }
  };
}